<template>
    <div
        :id="tableNameFormatted"
        :class="[
            'table-aips-latest',
            `${tableNameFormatted}`,
            `elevation-${elevation}`,
        ]"
    >
        <TableHeader
            v-if="tableHeader"
            :table-name="tableName"
            :allow-search="allowSearch"
            :columns="columnsLocal"
            :table-open="tableOpen"
            :has-table-data="!showInitialTableSlot"
            :table-collapse="tableCollapse"
            :toggle-columns="toggleColumns"
            :toggle-density="toggleDensity"
            :table-actions="tableActions"
            :search-details="searchDetails"
            :search-width="searchWidth"
            :table-bulk-actions="tableBulkActions"
            :show-bulk-actions="showBulkActions"
            @action-selected="$emit('action-selected', $event)"
            @apply-search-filter="applySearchFilter"
            @bulk-action-selected="$emit('bulk-action-selected', $event)"
            @column-toggled="columnToggled"
            @dense-data-toggle="denseDataToggle"
            @set-table-open="tableOpen = true"
            @toggle-filters-open="toggleFilters"
            @toggle-table-open="toggleTableOpen"
        >
            <template #tableHeaderName>
                <slot name="tableHeaderName"></slot>
            </template>
            <template #tableHeaderRight>
                <slot name="tableHeaderRight"></slot>
            </template>
            <template #tableMessage>
                <slot name="tableMessage"></slot>
            </template>
            <template #tableHeaderActionsPrepend>
                <slot name="tableHeaderActionsPrepend"></slot>
            </template>
            <template #tableHeaderActionsAppend>
                <slot name="tableHeaderActionsAppend"></slot>
            </template>
            <template #bulkActionBarPrepend>
                <slot name="bulkActionBarPrepend"></slot>
            </template>
        </TableHeader>
        <TableFilters
            :columns="columnsLocal"
            :filter-state="filterState"
            :show-filters="filterOpened && !disableFilters"
            :table-open="tableOpen"
            :filter-transition-name="filterTransitionName"
            :responsive-filter-dropdowns="responsiveFilterDropdowns"
            @apply-table-filters="applyTableFilters"
            @add-filter="addFilter"
            @remove-filter="removeFilter"
        />
        <div
            :id="`${tableNameFormatted}-table-scroll`"
            class="aips-table-wrap"
            :class="[
                {
                    disabled: disableTable,
                    'sticky-header': stickyHeader,
                    'no-border': !tableOpen,
                    'sticky-columns': stickyColumns,
                    'sticky-column-first':
                        stickyColumns == 'both' || stickyColumns == 'first',
                    'sticky-column-second': isSecondColumnSticky,
                    'sticky-column-last':
                        stickyColumns == 'both' || stickyColumns == 'last',
                    'no-header': !tableHeader,
                },
            ]"
            :style="setTableHeight"
            @scroll.passive="handleScroll"
        >
            <Transition name="slide-down">
                <div
                    v-if="tableOpen"
                    class="overflow-x-scroll"
                    :class="tableStyleClasses"
                >
                    <table class="aips-table">
                        <thead>
                            <TrAips class="table-aips-headers">
                                <template
                                    v-for="(column, index) in columnsLocal"
                                >
                                    <template v-if="column.showColumn">
                                        <ThAips
                                            v-if="column.headerCheckbox"
                                            :key="index + '-header-check'"
                                            :class="`${tableNameFormatted}-th-${index}`"
                                            :style="columnWidth(column.width)"
                                            :data-cy="`table.col.${index}.header`"
                                        >
                                            <AonCheckbox
                                                :remove-margins="true"
                                                style="
                                                    padding-top: 6px;
                                                    margin-top: -8px;
                                                "
                                                :is-checked="allChecked"
                                                :data-cy="`table.col.${index}.header.checkbox`"
                                                @clicked="allCheckedClicked"
                                            />
                                        </ThAips>
                                        <ThAipsSort
                                            v-else-if="
                                                sortColumns &&
                                                !column.hasCheckbox &&
                                                !column.allowPinning &&
                                                !column.disableSort
                                            "
                                            :key="index + '-header-sort'"
                                            v-tooltip="{
                                                content:
                                                    column.headerTooltip ||
                                                    column.locale,
                                                delay: { show: 500, hide: 0 },
                                            }"
                                            class="float-icon overflow-ellipsis"
                                            :class="`${columnAlignmentCss(
                                                column.alignColumn
                                            )}`"
                                            :style="columnWidth(column.width)"
                                            :column-name="column.value"
                                            :sort-state="sortState"
                                            :data-cy="`table.col.${index}.header`"
                                            @onSort="onSort"
                                        >
                                            <template
                                                v-if="column.headerWithSlot"
                                            >
                                                <div class="slot-holder">
                                                    <div
                                                        class="header-text-holder"
                                                    >
                                                        <span
                                                            :class="`${column.headerClass}`"
                                                            :data-cy="`table.col.${index}.header.text`"
                                                        >
                                                            {{ column.locale }}
                                                        </span>
                                                    </div>
                                                    <div
                                                        class="header-slot-holder"
                                                    >
                                                        <slot
                                                            :name="`${column.value}Header`"
                                                            :data-cy="`table.col.${index}.header.slot`"
                                                        ></slot>
                                                    </div>
                                                </div>
                                            </template>
                                            <span
                                                v-else
                                                :class="`${column.headerClass}`"
                                                :data-cy="`table.col.${index}.header.text`"
                                            >
                                                {{ column.locale }}
                                            </span>
                                        </ThAipsSort>
                                        <ThAips
                                            v-else
                                            :key="index + '-header'"
                                            v-tooltip="{
                                                content:
                                                    column.headerTooltip ||
                                                    column.locale,
                                                delay: { show: 500, hide: 0 },
                                            }"
                                            :class="`${tableNameFormatted}-td-${index} overflow-ellipsis ${columnAlignmentCss(
                                                column.alignColumn
                                            )}`"
                                            :style="columnWidth(column.width)"
                                            :data-cy="`table.col.${index}.header`"
                                        >
                                            <template
                                                v-if="column.headerWithSlot"
                                            >
                                                <div class="slot-holder">
                                                    <div
                                                        class="header-text-holder"
                                                    >
                                                        <span
                                                            :class="`${column.headerClass}`"
                                                            :data-cy="`table.col.${index}.header.text`"
                                                        >
                                                            {{ column.locale }}
                                                        </span>
                                                    </div>
                                                    <div
                                                        class="header-slot-holder"
                                                    >
                                                        <slot
                                                            :name="`${column.value}Header`"
                                                            :data-cy="`table.col.${index}.header.slot`"
                                                            :data="tData"
                                                        ></slot>
                                                    </div>
                                                </div>
                                            </template>
                                            <span
                                                v-else
                                                :class="`${column.headerClass}`"
                                                :data-cy="`table.${column.locale}-header.text`"
                                            >
                                                {{ column.locale }}
                                            </span>
                                        </ThAips>
                                    </template>
                                </template>
                            </TrAips>
                        </thead>
                        <tbody>
                            <template v-if="!emptyResultSet">
                                <TrAips
                                    v-for="(tData, rIndex) in tableData"
                                    :key="rIndex + '-row'"
                                    :class="[
                                        `row-${rIndex}`,
                                        { notIncluded: !tData.included },
                                        {
                                            selected:
                                                selectedRow === `row-${rIndex}`,
                                        },
                                    ]"
                                    :data-cy="`table.row.${rIndex}`"
                                >
                                    <template
                                        v-for="(column, index) in columnsLocal"
                                    >
                                        <TdAips
                                            v-if="column.showColumn"
                                            :key="index + '-data'"
                                            :align-css-class="
                                                columnAlignmentCss(
                                                    column.alignColumn
                                                )
                                            "
                                            :class="[
                                                `col-${index}`,
                                                `row-${rIndex}`,
                                                {
                                                    'checkbox-td':
                                                        column.hasCheckbox,
                                                    'allow-overflow':
                                                        column.allowOverflow ===
                                                        undefined
                                                            ? false
                                                            : column.allowOverflow,
                                                },
                                                tableDensity
                                            ]"
                                            :style="columnWidth(column.width)"
                                            :table-type="tableStyle"
                                            :padding-right="column.paddingRight"
                                            :data-cy="`table.row.${rIndex}.col.${index}`"
                                        >
                                            <AonCheckbox
                                                v-if="column.hasCheckbox"
                                                :remove-margins="true"
                                                :is-checked="
                                                    tData[column.checkboxValue]
                                                "
                                                :data-cy="`table.row.${rIndex}.col.${index}.checkbox`"
                                                @clicked="
                                                    tableRowChecked(tData)
                                                "
                                            />
                                            <div
                                                v-if="column.allowPinning"
                                                class="pin"
                                                style="text-align: center"
                                            >
                                                <div
                                                    class="disabled-holder"
                                                >
                                                    <font-awesome-icon
                                                        icon="fas fa-thumbtack"
                                                        class="icon grey01--text"
                                                        :class="{ 'active': tData.pinned }"
                                                        @click="
                                                            tableRowPinned(tData)
                                                        "
                                                    ></font-awesome-icon>
                                                    <div
                                                        v-if="disablePinning && !tData.pinned"
                                                        class="disabled-cover"
                                                        v-tooltip="{
                                                            content: disablePinningMessage,
                                                            delay: { show: 500, hide: 0 },
                                                        }"
                                                    ></div>
                                                </div>
                                            </div>
                                            <span
                                                v-tooltip="{
                                                    disabled:
                                                        column.disableTooltip,
                                                    content:
                                                        tData[column.tooltip] ||
                                                        tData[column.value],
                                                    delay: {
                                                        show: 500,
                                                        hide: 0,
                                                    },
                                                }"
                                                :class="[
                                                    {
                                                        'pl-6 overflow-ellipsis':
                                                            column.hasCheckbox,
                                                        'wrap-text': !denseData,
                                                    },
                                                    {
                                                        'no-value':
                                                            !tData[
                                                                column.value
                                                            ],
                                                    },
                                                ]"
                                                :data-cy="`table.row.${rIndex}.col.${index}.value`"
                                            >
                                                <template v-if="column.useSlot">
                                                    <slot
                                                        :data-cy="`table.row.${rIndex}.col.${index}.slot`"
                                                        :name="column.value"
                                                        :data="tData"
                                                    ></slot>
                                                </template>
                                                <template
                                                    v-else-if="column.link"
                                                >
                                                    <a
                                                        :href="
                                                            tData[
                                                                column.linkValue
                                                            ]
                                                        "
                                                        :data-cy="`table.row.${rIndex}.col.${index}.link`"
                                                        target="_blank"
                                                    >
                                                        {{
                                                            tData[column.value]
                                                        }}
                                                    </a>
                                                </template>
                                                <template v-else>
                                                    {{ tData[column.value] }}
                                                </template>
                                            </span>
                                        </TdAips>
                                    </template>
                                </TrAips>
                            </template>
                            <div :id="`${tableNameFormatted}-table-loader`"></div>
                            <template v-if="loading || forceClearTable">
                                <TrAips
                                    v-for="rIndex in pageSize"
                                    :key="rIndex + '-row'"
                                    :class="[`loading-row-${rIndex}`]"
                                    :data-cy="`table.loading.row.${rIndex}`"
                                >
                                    <template
                                        v-for="(column, index) in columnsLocal"
                                    >
                                        <TdAips
                                            v-if="column.showColumn"
                                            :key="index + '-data'"
                                            :class="[
                                                `col-${index}`,
                                                `row-${rIndex}`,
                                                tableDensity
                                            ]"
                                            :data-cy="`table.loading.row.${rIndex}.col.${index}`"
                                        >
                                            <AonSkeleton
                                                :loading="loading"
                                                :data="null"
                                                width="three-quarter"
                                                light
                                            />
                                        </TdAips>
                                    </template>
                                </TrAips>
                            </template>
                            <!-- <div
                                v-else-if="loading || forceClearTable"
                                class="py-10"
                            >
                                <span>
                                    <slot name="loadingRow">
                                        <Spinner
                                            :scale="0.5"
                                            class="center-fixed spinner-position"
                                        />
                                    </slot>
                                </span>
                            </div> -->
                        </tbody>
                    </table>
                </div>
            </Transition>
            <template
                v-if="
                    tableData.length === 0 && !loading && showInitialTableSlot
                "
                class="initial-slot-row no-hover"
                :data-cy="`table.initial-row`"
            >
                <slot name="initialState"> Initial content here </slot>
            </template>
            <template
                v-else-if="
                    tableData.length === 0 && !loading && !forceClearTable
                "
                class="no-results-row no-hover"
            >
                <p class="center extra-padding" :data-cy="`table.no-results`">
                    No results found.
                </p>
            </template>
        </div>
    </div>
</template>

<script>
import { nextTick } from 'vue';
import { debounce } from 'lodash-es';
import Spinner from '@moatmetrics/armory/src/components/base/AonSpinner.vue';
import AonCheckbox from '@moatmetrics/armory/src/components/base/forms/AonCheckbox.vue';
import AonSkeleton from '@moatmetrics/armory/src/components/AonSkeleton.vue';
import {
    flattenQueryState,
    hasQueryStateChanged,
} from '../../common/queryState.js';
import TdAips from './TdAips.vue';
import ThAips from './ThAips.vue';
import ThAipsSort from './ThAipsSort.vue';
import TrAips from './TrAips.vue';
import TableHeader from './TableHeader.vue';
import TableFilters from './TableFilters.vue';

/** EVENTS EMITTED
    - tableRowChecked(data)
        emitted from a row specific checkbox, if exists
        data: object, row data value
    - allCheckedClicked(checked)
        emitted from a header checkbox, if exists
        checked: boolean
    - loadData(params):
        emitted when ready to load more data
        params: object, contains all sort/filter/paging params
*/

export default {
    name: 'TableAips',
    components: {
        Spinner,
        TdAips,
        ThAips,
        ThAipsSort,
        TrAips,
        AonCheckbox,
        TableHeader,
        TableFilters,
        AonSkeleton,
    },
    props: {
        // only needed if have a header checkbox for select all/deselect all
        // value used to show checkbox as checked or not
        allChecked: {
            type: Boolean,
            default: false,
        },
        /** Show the search icon and functionality in the header */
        allowSearch: {
            type: Boolean,
            default: false,
        },
        /**
         * Definition of the columns in the table
         * Array of objects
         * EX:
         *  [{
         *      value: 'name',
                locale: 'Name',
                filterType: 'string',
                defaultFilter: { itemText: 'Starts With', itemValue: 'starts_with' },
                hasCheckbox: true,
                checkboxValue: '',
                showColumn: true,
                useSlot: true,
                width: 20,
                linkValue: 'lex_link',
                filters: [{ // Overrides the filter options available
                    itemText: 'Starts With', itemValue: 'starts_with',
                    itemText: 'Empty', itemValue: 'empty'
                }],
                headerTooltip: 'Header Tooltip Text Here',
                headerCheckbox: true,
                headerClass: 'underline',
                alignColumn: 'right',
                hideFromFilters: true,
                hideFromColToggle: false,
                paddingRight: '20px',
                disableTooltip: false,
                headerWithSlot: false,
                filterActive: false,
                allowOverflow: true,
                defaultFilterColumn: true,
                sortable: true
         *  }]
         */
        columns: {
            type: Array,
            default: () => [],
        },
        // update this prop when new data is available
        // array of objects to display in the rows of the table
        data: {
            type: Array,
            default: null,
        },
        /**
         * Default sort of table used for creating query string
         * Array of objects
         * EX:
         *  [{
         *      sortColumn: 'total_revenue',
         *      sortDirection: -1,
         *      sortPriority: 1
         *  }]
         */
        defaultSort: {
            type: Array,
            default: () => [],
        },
        defaultFilter: {
            type: Array,
            default: () => [],
        },
        disableFilters: {
            type: Boolean,
            default: false,
        },
        // Flag to disable the table from clicking, applies opacity
        disableTable: {
            type: Boolean,
            default: false,
        },
        elevation: {
            type: Number,
            default: 1,
            validator: (v) => [0, 1, 2, 3, 4, 5].includes(v),
        },
        // Flag for if there was an error loading data
        error: {
            type: Boolean,
            default: false,
        },
        // Used when usePageData is true
        filteredDataLength: {
            type: Number,
            default: 0,
        },
        // Force close the filters
        filtersClosed: {
            type: Boolean,
            default: false,
        },
        // Flag to force clear the table
        // Used when some other input/field is driving table content
        // EX: IP Scape Search Page - Patent Text Area search is driving table
        //      When user enters new patent ids, force clear the table before loading more data
        forceClearTable: {
            type: Boolean,
            default: false,
        },
        tableHeight: {
            type: [Number, String],
            default: '600px',
        },
        // Flag to tell table if loading data is in process
        loadingData: {
            type: Boolean,
            default: true,
        },
        // handle loading of data on your own
        // generally used if only one initial load of data, no filtering/sorting
        noPagingOrScroll: {
            type: Boolean,
            default: false,
        },
        // Number of records loaded per page load
        pageSize: {
            type: Number,
            default: 25,
        },
        refreshTableData: {
            type: Array,
            default: null,
        },
        searchDetails: {
            type: Object,
            default: () => {},
        },
        // Set the width of the search input
        searchWidth: {
            type: String,
            default: '400px',
        },
        selectedRow: {
            type: String,
            default: '',
        },
        /** Before table data is loaded, show some content in the table body
         *  EX: IP Scape Search Page
         */
        showInitialTableSlot: {
            type: Boolean,
            default: false,
        },
        // Add sort to columns
        sortColumns: {
            type: Boolean,
            default: true,
        },
        stickyHeader: {
            type: Boolean,
            default: false,
        },
        /**
         *
         * Freeze the first, last or both columns on scrolling. Also makes the header sticky
         * Note: The table-style prop needs to also be set to 'fixed-scrolling'
         * Valid values are:
         *  'both', 'first' or 'last'
         * Note: to make the second column sticky add 'stickyColumn: true' to the column props.
         *   This only works for the second column currently
         */
        stickyColumns: {
            type: String,
            default: null,
        },
        // Actions to add to the actions slider dropdown in the header of the table
        tableActions: {
            type: Array,
            default: () => [],
        },
        /**
         * Adds a action bar below the header
         * Array of objects
         * EX:
         *  [{
         *      itemText: 'Things', // action label
         *      disabled: true/false
         *      disabledMsg: '' // message to show in tooltip if disabled
         *      faIconStyle: 'fas' // icon style | fas,fal,far
         *      faIcon: 'fa-plus' // icon for action | must be imported at the project level
         *  }]
         */
        tableBulkActions: {
            type: Array,
            default: () => [],
        },
        showBulkActions: {
            type: Boolean,
            default: false,
        },
        /** Ability to collapse/open the table */
        tableCollapse: {
            type: Boolean,
            default: true,
        },
        tableHeader: {
            type: Boolean,
            default: true,
        },
        /** Name of table that exists in the header */
        tableName: {
            type: String,
            default: 'Table Name',
        },
        /**
         * 'auto'
         * 'fixed', (default)
         * 'fixed-scrolling'
         * 'flex',
         */
        tableStyle: {
            type: String,
            default: 'fixed',
        },
        // Add column toggle functionality
        toggleColumns: {
            type: Boolean,
            default: false,
        },
        // Add ability to toggle density of rows (1 line vs 3 lines)
        toggleDensity: {
            type: Boolean,
            default: false,
        },
        /**
         * Set the total data length
         * Only applies if usePageData === true
         */
        totalDataLength: {
            type: Number,
            default: 0,
        },
        /**
         * Set to false to use last row fetching of data
         */
        usePageData: {
            type: Boolean,
            default: true,
        },
        filterTransitionName: {
            type: String,
            default: 'slide-down',
        },
        enableHeaderHeightAdjustment: {
            type: Boolean,
            default: false,
        },
        headerHeightAdjustmentOffset: {
            type: String,
            default: '0px',
        },
        responsiveFilterDropdowns: {
            type: Boolean,
            default: false,
        },
        tableDensity: {
            type: String,
            default: 'moderate',
        },
        disablePinning: {
            type: Boolean,
            default: false,
        },
        disablePinningMessage: {
            type: String,
            default: 'Pinning Disabled',
        },
    },
    data() {
        return {
            columnsToToggle: [],
            lastPage: false,
            lastRowNum: 0,
            emptyResultSet: false,
            loading: false,
            tableData: this.data ? this.data : [],
            localError: false,
            filterOpened: false,
            filterState: null,
            pageState: {
                page: 0,
                pageSize: this.pageSize,
            },
            sortState: [],
            params: {},
            queryStateChanged: false,
            infiniteState: null,
            filtersOpen: false,
            tableOpen: true,
            denseData: true,
            tableColumns: [...this.columns],
            filterContainerHeight: 0,
            secondColumnPostion: [...this.columns][0]?.width || 0,
        };
    },
    computed: {
        isSecondColumnSticky() {
            return !!this.tableColumns[1]?.stickyColumn
        },
        columnsLocal: {
            get() {
                if (!this.stickyColumns) {
                    return this.tableColumns;
                }
                if (['both', 'first'].includes(this.stickyColumns)) {
                    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                    this.tableColumns[0].hideFromColToggle = true;
                }
                if (['both', 'last'].includes(this.stickyColumns)) {
                    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                    this.tableColumns[
                        this.tableColumns.length - 1
                    ].hideFromColToggle = true;
                }
                if (this.isSecondColumnSticky) {
                    this.tableColumns[1].hideFromColToggle = true;
                }
                return this.tableColumns;
            },
            set(val) {
                this.tableColumns = val;
            },
        },
        getDataSetSize() {
            if (this.isFiltering) {
                return this.filteredDataLength;
            }

            return this.totalDataLength;
        },
        hasMorePages() {
            return (
                this.pageState.page * this.pageState.pageSize <
                this.getDataSetSize
            );
        },
        isFiltering() {
            return this.filterState !== null && this.filterState.length > 0;
        },
        tableStyleClasses() {
            let classes = this.tableStyle.split('-');

            return classes;
        },
        isError() {
            return this.localError && this.error;
        },
        newDataLength() {
            return this.tableData.length - this.data.length;
        },
        setTableHeight() {
            if (
                !this.tableHeight ||
                (!this.stickyHeader && !this.stickyColumns)
            ) {
                return null;
            }
            const validUnits = ['%', 'px', 'rem', 'em', 'pt', 'vh'];
            const heightAndUnit = `${this.tableHeight}`.match(/\d+|\D+/g);

            if (heightAndUnit.length === 1) {
                return `max-height: calc(${this.tableHeight}% - ${this.filterContainerHeight}px);`;
            }

            let widthUnit = heightAndUnit[1];

            if (validUnits.includes(widthUnit)) {
                return `max-height: calc(${this.tableHeight} - ${this.filterContainerHeight}px);`;
            }

            return `max-height: calc(${heightAndUnit[0]}% - ${this.filterContainerHeight}px);`;
        },
        tableNameFormatted() {
            return `${this.tableName.replaceAll(' ', '-').toLowerCase()}-table`;
        },
    },
    watch: {
        columns() {
            this.tableColumns = [...this.columns];
            if (this.isSecondColumnSticky) {
                this.secondColumnPostion = [...this.columns][0]?.width || 0
            }
        },
        filtersClosed(v) {
            if (v) {
                this.filterOpened = false;
                this.filterState = [];
            }
        },
        error() {
            this.localError = this.error;
            this.loading = false;
        },
        loadingData() {
            if (!this.tableOpen) {
                return;
            }
            if (!this.loadingData) {
                if (
                    !this.data ||
                    this.data.length === 0 ||
                    this.data.length === this.tableData.length
                ) {
                    this.noMoreTableData();
                } else {
                    this.loadMoreTableData();
                }
                this.loading = false;
            } else {
                this.loading = true;
            }
        },
        forceClearTable() {
            if (this.forceClearTable) {
                this.resetTable();
            }
        },
        refreshTableData: {
            handler: function (newVal) {
                if (newVal) {
                    this.tableData = newVal;
                }
            },
            deep: true,
        },
    },
    async mounted() {
        if (this.noPagingOrScroll) return;
        if (this.usePageData) {
            this.pageState.page = this.pageState.page + 1;
        }
        this.filterState = this.defaultFilter;

        if (!this.stickyHeader) {
            document.addEventListener('scroll', this.handleScroll);
        }

        this.queryChanged();
        this.setFilterContainerHeight()
    },
    methods: {
        columnAlignmentCss(alignment) {
            if (!alignment || alignment === 'left') {
                return 'text-left';
            }
            if (alignment === 'center') {
                return 'text-center';
            }
            if (alignment === 'right') {
                return 'text-right';
            }
        },
        columnWidth(width) {
            if (!width) {
                return null;
            }

            const validUnits = ['%', 'px', 'rem', 'em', 'pt', 'vw'];
            const widthAndUnit = `${width}`.match(/\d+|\D+/g);
            let widthUnit;

            if (widthAndUnit.length === 1) {
                return `width: ${width}%`;
            }

            widthUnit = widthAndUnit[1];

            if (validUnits.includes(widthUnit)) {
                return `width: ${width}`;
            }

            return `width: ${widthAndUnit[0]}%`;
        },
        handleScroll: debounce(function () {
            if (
                !this.tableOpen ||
                this.loading ||
                this.noPagingOrScroll ||
                this.showInitialTableSlot ||
                this.lastPage ||
                !this.tableNameFormatted
            ) {
                return;
            }
            let table = document.getElementById(this.tableNameFormatted);
            let element = table
                .querySelector(`#${this.tableNameFormatted}-table-scroll`)
                .getBoundingClientRect();
            let loader = table
                .querySelector(`#${this.tableNameFormatted}-table-loader`)
                .getBoundingClientRect();
            if (element.bottom > loader.top) {
                if (this.usePageData) {
                    this.nextPage();

                    return;
                }
                this.queryChanged();
            }
        }, 250),
        nextPage() {
            const newPage = this.pageState.page + 1;

            if (this.hasMorePages) {
                this.pageState.page = newPage;
                this.queryChanged();
            }
        },
        columnToggled(val) {
            this.columnsLocal.find(
                (col) => col.locale === val.itemText
            ).showColumn = val.checked;
        },
        denseDataToggle() {
            this.denseData = !this.denseData;
        },
        applySearchFilter(filter) {
            this.filterState = [{ search: true, ...filter }];
            this.onFilter();
        },
        applyTableFilters(filters) {
            this.filterState = filters.map((a) => ({ ...a }));
            this.onFilter();
        },
        onFilter() {
            if (!this.filterState) {
                this.filterState = [];
            }
            this.pageState.page = 0;
            this.queryChanged();
        },
        onSort(detail) {
            this.pageState.page = 0;
            let sortColumnState = {
                sortColumn: detail.sortColumn,
            };

            sortColumnState['sortDirection'] = detail.sortDirection;
            sortColumnState['sortPriority'] = null;
            this.sortState = [];

            if (sortColumnState.sortDirection !== 0) {
                this.sortState.push(sortColumnState);
            }
            if (this.noPagingOrScroll) {
                const queryString = this.getParams();

                this.$emit('sort-change', queryString);

                return;
            }
            this.queryChanged();
        },
        getParams() {
            const params = {
                sortState: this.sortState,
                filterState: this.filterState || [],
            };

            if (this.usePageData) {
                params.pageState = this.pageState;
            }
            return this.computeParams(params);
        },
        queryChanged: debounce(function () {
            const queryString = this.getParams();
            // Query params have changed, so clear out data and reset paging values
            if (this.queryStateChanged) {
                this.lastPage = false;
                this.tableData = [];
            }

            // If this is the last page of data, don't try to load more
            if (this.lastPage) {
                return;
            }

            // If we don't want to load table data initially, emit the params to use
            if (this.showInitialTableSlot) {
                this.$emit('initialParams', queryString);
                return;
            }

            this.emptyResultSet = false;
            this.loading = true;

            /**
             * Triggers when the queryChanged method is called
             *
             * ```jsx
             * 'usePageData' set to true:
             * {
             *  sortState: this.sortState,
             *  filterState: this.filterState,
             *  pageState: this.pageState
             * }
             *
             * 'usePageData' set to false:
             * {
             *  sortState: this.sortState,
             *  filterState: this.filterState
             * }
             * ```
             *
             * @property {Object} value
             */
            this.$emit('loadData', {
                params: queryString,
                paramsChanged: this.queryStateChanged,
            });
            this.setFilterContainerHeight(this.filterOpened);
        }, 500),
        computeParams(queryState) {
            const params = flattenQueryState(
                queryState,
                this.defaultSort,
                this.defaultFilter
            );
            this.queryStateChanged = false;

            if (hasQueryStateChanged(this.params, params)) {
                this.lastRowNum = 0;
                this.queryStateChanged = true;
                this.params = { ...params };
            }

            params['page_size'] = this.pageSize;
            params['last_row_num'] = this.lastRowNum;
            params['page'] = Math.ceil(this.lastRowNum / this.pageSize) + 1;

            return params;
        },
        resetFilters(leaveFilters = false) {
            if (!leaveFilters) {
                this.filterOpened = false;
            }
            this.filterState = null;
            this.pageState.page = 0;
            this.$emit('filterOpened', this.filterOpened);
            this.queryChanged();
        },
        noMoreTableData() {
            this.lastPage = true;
            this.lastRowNum = 0;
            // Check length of new data
            // If length of 0, set the table to display empty results
            if (!this.error && this.data.length === 0) {
                this.emptyResultSet = true;
            }
        },
        loadMoreTableData() {
            if (this.data.length - this.tableData.length < this.pageSize) {
                this.lastPage = true;
            }
            this.tableData = [...this.data];
            this.lastRowNum = this.tableData.length;
        },
        resetTable() {
            this.emptyResultSet = false;
            this.tableData = [];
            this.lastRowNum = 0;
            this.lastPage = false;
            this.filterState = [];

            this.pageState = {
                page: 0,
                pageSize: this.pageSize,
            };
        },
        tableRowChecked(data) {
            this.$emit('tableRowChecked', data);
        },
        allCheckedClicked() {
            this.$emit('allCheckedClicked', !this.allChecked);
        },
        tableRowPinned(data) {
            this.$emit('tableRowPinned', data);
        },
        saveSearch() {
            // console.log('TODO: save search')
        },
        toggleTableOpen() {
            this.tableOpen = !this.tableOpen;
            this.$emit('toggle-table-open', this.tableOpen);
        },
        addFilter(numFilters) {
            this.$emit('add-filter', numFilters)
        },
        removeFilter(numFilters) {
            this.$emit('remove-filter', numFilters)
        },
        async setFilterContainerHeight(open) {
            if (this.enableHeaderHeightAdjustment) {
                await nextTick()
                let tableWrap = document.getElementsByClassName('aips-table-wrap')[0]
                let filtersHeight = document.getElementsByClassName('table-filters-container')[0].offsetHeight
                let tableFilterChips = document.getElementsByClassName('table-filter-chips')[0]?.offsetHeight ?? 0

                if (open) {
                    tableWrap.style.setProperty('height', `calc(100% - ${this.headerHeightAdjustmentOffset} - ${filtersHeight}px)`);
                } else {
                    tableWrap.style.setProperty('height', `calc(100% - ${this.headerHeightAdjustmentOffset} - ${tableFilterChips}px)`);
                }
            }
        },
        toggleFilters(val) {
            this.filterOpened = !this.filterOpened;
            this.$emit('toggle-filters-open', val)
            this.setFilterContainerHeight(val);
        },
    },
};
</script>

<style lang="scss" scoped>
div.table-aips-latest {
    width: 100%;
    color: $navy01;
    border: solid 1px $grey05;
    border-radius: 10px;
    background: white;

    .extra-padding {
        padding: 100px 0 100px 0;
    }

    .no-value {
        display: none;
    }

    #table-loader {
        height: 2px;
    }

    div.aips-table-wrap {
        border-bottom-left-radius: inherit;
        border-bottom-right-radius: inherit;
        border-top: solid 1px $grey05;
        //overflow: auto; // prevents scrollbar from appearing past rounded border
        transform: translate3d(
            0,
            0,
            0
        ); // hack to make overflow-hidden work in safari/webkit
        min-height: 100px;

        &.no-header {
            border-top-left-radius: inherit;
            border-top-right-radius: inherit;
            border-top: none;
        }

        div.overflow-x-scroll {
            // flip container to put scrollbar at top
            transform: rotateX(180deg);
            // un-rotate container so contents are right-side up
            & > * {
                transform: rotateX(180deg);
            }

            /* fixed table styles */
            &.fixed {
                overflow-x: unset;

                .aips-table {
                    width: 100%;
                    table-layout: fixed;
                }
            }

            &.scrolling {
                overflow-x: auto;
            }
        }

        table.aips-table {
            /* common table styles */
            border-collapse: separate;
            min-width: 100%;

            thead,
            tbody,
            tr {
                width: 100%;
            }

            thead tr th {
                border-right: solid 1px $grey05;
            }

            tbody tr {
                &:not(.no-hover):hover {
                    background: $grey07 !important;

                    td,
                    th {
                        background: $grey07 !important;
                    }
                }

                &:last-of-type {
                    td {
                        border-bottom: none;
                    }
                }
                &.selected {
                    background: $grey07 !important;
                }
            }

            th,
            td {
                font-family: $font-family;
                line-height: normal;
                border-bottom: solid 1px $grey05;

                &:first-of-type {
                    padding-left: 32px;
                }

                &:last-of-type {
                    padding-right: 32px;
                }

                &.float-icon {
                    padding-right: 32px;
                }
                .shrink-width {
                    width: calc(100% - 20px);
                }
            }

            td {
                .text-left {
                    text-align: left;
                }

                .text-center {
                    text-align: center;
                }

                .text-right {
                    text-align: right;
                }
            }

            th {
                &.text-left {
                    text-align: left;
                }

                &.text-center {
                    text-align: center;
                }

                &.text-right {
                    text-align: right;
                }
            }

            .th-aips-sort,
            .th-aips {
                padding: 12px;
                font-weight: 700;
                font-size: 14px;
                line-height: 18px;
                height: 42px;
            }

            .table-aips-item {
                font-size: 14px;
                font-weight: 400;
                color: $navy01;
                align-items: center;
                white-space: nowrap;
                overflow: hidden;
                vertical-align: middle;
                position: relative;
                &.allow-overflow {
                    overflow: visible;
                }
            }

            .table-aips-filter-row {
                background: $grey07;
                th,
                td {
                    border-right: none;
                    vertical-align: middle;
                }
            }

            .table-aips-filter-item {
                margin: 0;

                &.compact {
                    padding: 16px 12px;

                }

                &.moderate {
                    padding: 21px 12px;
                }

                &.comfy {
                    padding: 26px 12px;
                }

                &:first-of-type {
                    padding-left: 32px;
                }

                &:last-of-type {
                    padding-right: 32px;
                    .filter-selection-container {
                        left: auto;
                        right: 0px;
                    }
                }

                &.float-icon {
                    padding-right: 32px;
                }
            }

            .table-aips-headers {
                align-items: center;
                cursor: pointer;
                border-top: solid 1px $grey05;

                .slot-holder {
                    display: flex;
                }

                .table-aips-header {
                    color: $navy01;
                    cursor: pointer;

                    &.sort-on {
                        border-bottom-color: $peacock;
                        color: $peacock;
                    }

                    &:last-of-type {
                        border-right: none;
                    }

                    &.disable {
                        pointer-events: none;
                    }

                    &:hover {
                        background: $grey07;
                    }

                    &.float-icon {
                        padding-right: 32px;
                    }
                }
            }

            &.auto {
                table-layout: auto;

                th,
                td {
                    width: auto !important;
                }
            }

            .checkbox-td {
                > div:first-child .row-holder {
                    display: flex;
                    align-items: center;
                }
            }

            .checkbox-td > div:first-child {
                display: flex;
                align-items: center;
            }

            .wrap-text {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;

                @supports (-webkit-line-clamp: 2) {
                    white-space: initial;
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                }
            }
        }
        &.sticky-columns {
            overflow-y: auto;
            .overflow-x-scroll.scrolling {
                overflow-x: unset;
            }

            tr {
                background-color: #eee;

                th {
                    position: sticky;
                    top: 0;
                    background: $white;
                    z-index: 998;
                    box-shadow: 0px 3px 4px 0px $grey05;
                }
            }

            &.sticky-column-first {
                tr {
                    td:first-child {
                        position: sticky;
                        background: $white;
                        z-index: 997;
                    }

                    th:first-child {
                        z-index: 999;
                    }

                    th:first-child,
                    td:first-child {
                        left: 0;
                    }

                    td:first-child {
                        border-right: solid 1px $grey05;
                    }
                }
            }
            &.sticky-column-second {
                tr {
                    td:nth-child(2) {
                        position: sticky;
                        background: $white;
                        z-index: 997;
                    }

                    th:nth-child(2) {
                        z-index: 999;
                    }

                    th:nth-child(2),
                    td:nth-child(2) {
                        left: v-bind(secondColumnPostion);
                    }

                    td:first-child {
                        border-right: none;
                    }

                    td:nth-child(2) {
                        border-right: solid 1px $grey05;
                    }
                }
            }
            &.sticky-column-last {
                tr {
                    td:last-child {
                        position: sticky;
                        background: $white;
                        z-index: 997;
                    }

                    th:last-child {
                        z-index: 999;
                    }

                    th:last-child,
                    td:last-child {
                        right: 0;
                    }

                    th:nth-last-child(2) {
                        border-right: none;
                    }

                    th:last-child {
                        border-left: solid 1px $grey05;
                    }

                    td:last-child {
                        border-left: solid 1px $grey05;
                    }
                }
            }
        }
        &.sticky-header {
            overflow-y: auto;
            .overflow-x-scroll.scrolling {
                overflow-x: unset;
            }
            th {
                position: sticky;
                top: 0;
                background: white;
                z-index: 999;
                box-shadow: 0px 3px 4px 0px $grey05;
            }
        }
        &.no-border {
            border-top: none;
        }
    }

    .col-toggle {
        width: 100%;
    }

    .table-showing-count {
        margin-top: 20px;
        text-align: center;
    }

    .total-items {
        position: relative;
        p {
            width: 180px;
            position: absolute;
            bottom: 0;
        }
    }

    .disabled {
        opacity: 0.5;
        pointer-events: none;
    }

    p.center {
        text-align: center;
    }

    .spinner-position {
        top: auto;
    }

    .w-inherit {
        width: inherit;
    }

    .pin {
        position: relative;
        cursor: pointer;
        .icon {
            opacity: 0.5;
            &.active {
                opacity: 1;
            }
        }

        .disabled-holder {
            position: relative;
        }

        .disabled-cover {
            width: 100%;
            height: 100%;
            position: absolute;
            top: 0;
            left: 0;
            cursor: initial;

            &:before {
                content: '';
                display: block;
                width: 100%;
                height: 2px;
                background: $grey02;

                position: absolute;
                top: 40%;
                left: 0;

                transform: translateY(-50%);
            }
        }
    }
}
.slide-down-enter-active {
    transition: all 0.3s ease-out;
}

.slide-down-leave-active {
    transition: all 0.2s ease-in;
}

.slide-down-enter-from,
.slide-down-leave-to {
    transform: translateY(-20px);
    opacity: 0;
}
</style>
<style lang="scss">
.allow-overflow {
    div.overflow-ellipsis {
        overflow: visible;
    }
}
</style>
